/**
 * Bug Report Form Component
 * Structured form for reporting bugs and technical issues
 */

import React, { useState, useEffect } from 'react';
import { cn } from '../../../lib/utils';
import { Feedback, BugReportFeedback } from '../../../types/feedback';
import ScreenshotButton from '../ScreenshotButton';

interface BugReportFormProps {
  data: Partial<Feedback>;
  onChange: (data: Partial<BugReportFeedback>) => void;
  validation: { isValid: boolean; errors: Record<string, string> };
  currentStep: number;
  totalSteps: number;
  enableScreenshot: boolean;
}

const BugReportForm: React.FC<BugReportFormProps> = ({
  data,
  onChange,
  validation,
  currentStep,
  enableScreenshot
}) => {
  const bugData = data as Partial<BugReportFeedback>;
  const [steps, setSteps] = useState<string[]>(bugData.stepsToReproduce || ['']);
  const [screenshot, setScreenshot] = useState<any>(null);

  useEffect(() => {
    // Auto-populate browser info
    onChange({
      ...bugData,
      type: 'bug-report',
      browserInfo: {
        name: getBrowserName(),
        version: getBrowserVersion(),
        os: getOperatingSystem()
      }
    });
  }, []);

  const severityLevels = [
    {
      key: 'low',
      label: 'Low',
      description: 'Minor issue that doesn\'t affect core functionality',
      color: 'text-sage-600',
      bgColor: 'bg-sage-50 border-sage-300'
    },
    {
      key: 'medium',
      label: 'Medium',
      description: 'Issue affects functionality but has workarounds',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50 border-amber-300'
    },
    {
      key: 'high',
      label: 'High',
      description: 'Significant issue that impacts user experience',
      color: 'text-coral-600',
      bgColor: 'bg-coral-50 border-coral-300'
    },
    {
      key: 'critical',
      label: 'Critical',
      description: 'Severe issue that prevents normal operation',
      color: 'text-red-600',
      bgColor: 'bg-red-50 border-red-300'
    }
  ];

  const handleStepChange = (index: number, value: string) => {
    const newSteps = [...steps];
    newSteps[index] = value;
    setSteps(newSteps);
    onChange({
      ...bugData,
      stepsToReproduce: newSteps.filter(step => step.trim())
    });
  };

  const addStep = () => {
    setSteps([...steps, '']);
  };

  const removeStep = (index: number) => {
    if (steps.length > 1) {
      const newSteps = steps.filter((_, i) => i !== index);
      setSteps(newSteps);
      onChange({
        ...bugData,
        stepsToReproduce: newSteps.filter(step => step.trim())
      });
    }
  };

  if (currentStep === 1) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-serif text-slate-800 mb-2">
            Report a Bug
          </h3>
          <p className="text-sm text-slate-600">
            Help us fix issues by providing detailed information
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label htmlFor="bug-title" className="block text-sm font-medium text-slate-700 mb-2">
              Bug Title *
            </label>
            <input
              id="bug-title"
              type="text"
              value={bugData.title || ''}
              onChange={(e) => onChange({ ...bugData, title: e.target.value })}
              placeholder="Brief summary of the issue"
              className={cn(
                'input-minimal',
                validation.errors.title && 'border-coral-300 focus:border-coral-400'
              )}
            />
            {validation.errors.title && (
              <p className="text-sm text-coral-600 mt-1">{validation.errors.title}</p>
            )}
          </div>

          <div>
            <label htmlFor="bug-description" className="block text-sm font-medium text-slate-700 mb-2">
              Description *
            </label>
            <textarea
              id="bug-description"
              rows={4}
              value={bugData.description || ''}
              onChange={(e) => onChange({ ...bugData, description: e.target.value })}
              placeholder="Detailed description of what went wrong"
              className={cn(
                'input-minimal resize-none',
                validation.errors.description && 'border-coral-300 focus:border-coral-400'
              )}
            />
            {validation.errors.description && (
              <p className="text-sm text-coral-600 mt-1">{validation.errors.description}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Severity Level
            </label>
            <div className="grid grid-cols-2 gap-3">
              {severityLevels.map((level) => (
                <button
                  key={level.key}
                  onClick={() => onChange({ ...bugData, severity: level.key as BugReportFeedback['severity'] })}
                  className={cn(
                    'p-3 border-2 rounded-lg text-left transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2',
                    bugData.severity === level.key
                      ? level.bgColor + ' border-current'
                      : 'border-stone-200 hover:border-stone-300'
                  )}
                >
                  <div className={cn('font-medium mb-1', level.color)}>
                    {level.label}
                  </div>
                  <p className="text-xs text-slate-600 leading-snug">
                    {level.description}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-serif text-slate-800 mb-2">
          Steps and Details
        </h3>
        <p className="text-sm text-slate-600">
          Help us reproduce and fix the issue
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Steps to Reproduce
          </label>
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={index} className="flex gap-3">
                <div className="flex-shrink-0 w-6 h-6 bg-terracotta text-white text-sm rounded-full flex items-center justify-center mt-3">
                  {index + 1}
                </div>
                <div className="flex-1">
                  <textarea
                    value={step}
                    onChange={(e) => handleStepChange(index, e.target.value)}
                    placeholder={`Step ${index + 1}: Describe what you did...`}
                    className="input-minimal resize-none"
                    rows={2}
                  />
                </div>
                {steps.length > 1 && (
                  <button
                    onClick={() => removeStep(index)}
                    className="flex-shrink-0 p-1 text-slate-400 hover:text-coral-600 mt-3"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18"/>
                      <path d="M6 6l12 12"/>
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={addStep}
            className="mt-2 text-sm text-terracotta hover:text-terracotta-dark flex items-center gap-1"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14"/>
              <path d="M5 12h14"/>
            </svg>
            Add Step
          </button>
        </div>

        <div>
          <label htmlFor="expected-behavior" className="block text-sm font-medium text-slate-700 mb-2">
            Expected Behavior
          </label>
          <textarea
            id="expected-behavior"
            rows={2}
            value={bugData.expectedBehavior || ''}
            onChange={(e) => onChange({ ...bugData, expectedBehavior: e.target.value })}
            placeholder="What did you expect to happen?"
            className="input-minimal resize-none"
          />
        </div>

        <div>
          <label htmlFor="actual-behavior" className="block text-sm font-medium text-slate-700 mb-2">
            Actual Behavior
          </label>
          <textarea
            id="actual-behavior"
            rows={2}
            value={bugData.actualBehavior || ''}
            onChange={(e) => onChange({ ...bugData, actualBehavior: e.target.value })}
            placeholder="What actually happened instead?"
            className="input-minimal resize-none"
          />
        </div>

        {/* Screenshot Button */}
        {enableScreenshot && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Add a screenshot to help illustrate the issue
            </label>
            <ScreenshotButton
              onScreenshotCapture={(screenshotData) => {
                setScreenshot(screenshotData);
                onChange({
                  ...bugData,
                  screenshot: screenshotData.thumbnail
                });
              }}
              existingScreenshot={screenshot?.thumbnail}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Helper functions for browser detection
function getBrowserName(): string {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Chrome')) return 'Chrome';
  if (userAgent.includes('Firefox')) return 'Firefox';
  if (userAgent.includes('Safari')) return 'Safari';
  if (userAgent.includes('Edge')) return 'Edge';
  return 'Unknown';
}

function getBrowserVersion(): string {
  const userAgent = navigator.userAgent;
  const match = userAgent.match(/(Chrome|Firefox|Safari|Edge)\/([0-9.]+)/);
  return match ? match[2] : 'Unknown';
}

function getOperatingSystem(): string {
  const userAgent = navigator.userAgent;
  if (userAgent.includes('Mac')) return 'macOS';
  if (userAgent.includes('Windows')) return 'Windows';
  if (userAgent.includes('Linux')) return 'Linux';
  if (userAgent.includes('Android')) return 'Android';
  if (userAgent.includes('iOS')) return 'iOS';
  return 'Unknown';
}

export default BugReportForm;